import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Card,
  Link,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Link as LinkIcon } from '@mui/icons-material';
import moment from 'moment-timezone';
import colorsStyles from '../colors.styles';
import EditableVideoThumbnail from './EditableVideoThumbnail';
import { hasTemplateMedia, isEditableVideoTemplate } from './Template.model';

export default function TemplateItem({ template, onClick }) {
  const {
    id,
    type,
    source,
    picture,
    message,
    link,
    category,
    topic,
    subtopic,
    title,
    description,
  } = template;
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const replacePlaceHolders = (templateCategory, templateMessage) => {
    if (templateCategory !== 'fitb') return templateMessage;
    return templateMessage.split(/(\{[^}]+\})/g).map((part, index) => {
      if (/\{[^}]+\}/.test(part)) {
        // eslint-disable-next-line react/no-array-index-key
        return <BlankSpace key={index} placeholder={part.slice(1, -1)} />;
      }
      return part;
    });
  };

  const copyToClipboard = (e) => {
    navigator.clipboard.writeText(id);
    setOpenSnackbar(true);
    e.stopPropagation();
  };

  const showMedia = hasTemplateMedia(template);

  const styles = {
    card: {
      padding: '16px',
      borderRadius: '18px',
      cursor: 'pointer',
    },
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '8px',
    },
    thumbnail: {
      width: '134px', // from Figma
      height: '134px',
      objectFit: 'cover',
      borderRadius: '8px',
    },
    editableVideoThumbnail: {
      borderRadius: '8px',
      overflow: 'hidden',
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      maxWidth: showMedia ? 'calc(100% - 150px)' : '100%',
    },
    textContainer: {
      maxHeight: '40px',
      overflow: 'clip',
      marginBottom: '5px',
    },
    text: {
      display: '-webkit-box',
      WebkitLineClamp: link !== null ? 2 : 7,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'pre-wrap',
    },
    linkContainer: { display: 'flex', alignItems: 'center', gap: '4px' },
    link: {
      color: colorsStyles.base.walmart_blue,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      display: 'inline-block',
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    linkIcon: {
      transform: 'rotate(-45deg)',
      color: colorsStyles.text.gray,
    },
    idButton: { textTransform: 'none' },
    detailsContainer: { display: 'flex', justifyContent: 'space-between' },
    detailsContainerItem: { display: 'flex', alignItems: 'end', gap: '4px' },
    detailsTitle: {
      textTransform: 'uppercase',
      fontSize: '11px',
      lineHeight: 'normal',
      fontWeight: '800',
    },
    detailsContent: {
      fontSize: '14px',
      lineHeight: 'normal',
      fontWeight: '400',
    },
  };

  const dateValuesLabel = (() => {
    const startDate = template.start_date
      ? moment(template.start_date).tz('America/Chicago').format('MM/DD/YY')
      : null;

    const endDate = template.end_date
      ? moment(template.end_date).tz('America/Chicago').format('MM/DD/YY')
      : null;

    if (!startDate && !endDate) {
      return 'Evergreen';
    }

    return `${startDate ?? ''} - ${endDate ?? 'Evergreen'}`;
  })();

  return (
    <>
      <Card sx={styles.card} onClick={onClick}>
        <Box sx={styles.container}>
          <Box display="flex" gap={2}>
            {showMedia &&
              (isEditableVideoTemplate(template) ? (
                <EditableVideoThumbnail
                  size="list"
                  sx={styles.editableVideoThumbnail}
                />
              ) : (
                <img
                  style={styles.thumbnail}
                  alt={`template ${id}`}
                  src={type === 'video' ? picture : source}
                />
              ))}
            <Box sx={styles.contentContainer}>
              {category !== 'pl' ? (
                <Box>
                  <Typography variant="body2" component="span" sx={styles.text}>
                    {replacePlaceHolders(category, message)}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box>
                    <Box sx={styles.textContainer}>
                      <Typography variant="body2" sx={styles.text}>
                        <b>Title:</b> {title}
                      </Typography>
                    </Box>
                    <Box sx={styles.textContainer}>
                      <Typography variant="body2" sx={styles.text}>
                        <b>Description:</b> {description}
                      </Typography>
                    </Box>
                    <Box sx={styles.textContainer}>
                      <Typography variant="body2" sx={styles.text}>
                        {replacePlaceHolders(category, message)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={styles.linkContainer}>
                    <LinkIcon sx={styles.linkIcon} />
                    <Link href={link} underline="hover" sx={styles.link}>
                      {link}
                    </Link>
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Stack gap={1}>
            <Box sx={{ ...styles.detailsContainerItem }}>
              <Typography sx={styles.detailsTitle}>Template ID:</Typography>
              <Typography sx={styles.detailsContent}>{id}</Typography>
              <Tooltip title="Click to copy the ID" placement="top" arrow>
                <ContentCopy
                  fontSize="small"
                  cursor="pointer"
                  onClick={copyToClipboard}
                />
              </Tooltip>
            </Box>
            <Box sx={styles.detailsContainer}>
              <Box sx={styles.detailsContainerItem}>
                <Typography sx={styles.detailsTitle}>Topic:</Typography>
                <Typography sx={styles.detailsContent}>{topic.name}</Typography>
              </Box>
              <Box sx={styles.detailsContainerItem}>
                <Typography sx={styles.detailsTitle}>Subtopic:</Typography>
                <Typography sx={styles.detailsContent}>
                  {subtopic.name}
                </Typography>
              </Box>
              <Box sx={styles.detailsContainerItem}>
                <Typography sx={styles.detailsTitle}>Date:</Typography>
                <Typography sx={styles.detailsContent}>
                  {dateValuesLabel}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        message="ID copied to clipboard"
        onClose={() => setOpenSnackbar(false)}
      />
    </>
  );
}

function BlankSpace({ placeholder }) {
  return (
    <Box
      sx={{
        display: 'inline-block',
        borderRadius: '5px',
        paddingX: '5px',
        backgroundColor: colorsStyles.base.very_light_gray,
      }}
    >
      <Typography variant="body2" color={colorsStyles.text.gray}>
        {placeholder}
      </Typography>
    </Box>
  );
}

TemplateItem.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    source: PropTypes.string,
    cesdk_scene_url: PropTypes.string,
    picture: PropTypes.string,
    message: PropTypes.string,
    link: PropTypes.string,
    category: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    topic: PropTypes.shape({
      name: PropTypes.string,
    }),
    subtopic: PropTypes.shape({
      name: PropTypes.string,
    }),
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

BlankSpace.propTypes = {
  placeholder: PropTypes.string,
};

BlankSpace.defaultProps = {
  placeholder: 'BLANK',
};
